// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// DRAFT AI-assistance notice (IP, License & Attribution Policy v1.3 §12d-1):
// This content was prepared with the assistance of AI tools and is, on this
// branch, a DRAFT pending human review. On the human-approved merge of its
// Merge Request this notice flips to the published variant (§12d-2).
// Bu icerik yapay zeka destekli araclarla hazirlanmis bir TASLAKTIR (§12d-1);
// insan onayiyla birlestirilmesinde yayimlanmis surumune (§12d-2) donusur.

/**
 * Gateway postMessage transport (adapted from AU0008 ADR-005).
 *
 * The AU0009 widget runs in an external frame with NO direct ZOHO.CREATOR
 * access; every Zoho Creator interaction is relayed by the Widget API Gateway
 * over postMessage. This module implements the client half of that channel:
 *   - explicit target origin on send, never "*";
 *   - inbound origin check + messageUUID correlation on replies;
 *   - single-use correlation ids, teardown on match, stray/late replies ignored;
 *   - a per-request timeout with a bounded reject (ADR-002 P4 bounded give-up);
 *   - ancestor/sibling-frame broadcast, because frames are not addressable.
 *
 * The environment (frame enumeration, clock, uuid, listener) is injected so the
 * transport is unit-testable without a real browser or Gateway (no hidden
 * globals; ISO/IEC 5055 testability & bounded control flow).
 */

import type { BridgeOriginConfig } from "./config";
import type { CallDescriptor, InterpretedResponse, RequestEnvelope } from "./envelope";
import { PROTOCOL_VERSION, makeRequestEnvelope, readMessageUUID } from "./envelope";
import type { BridgeError } from "./errors";
import { makeBridgeError, isBridgeError } from "./errors";

/** A minimal postMessage target - a frame's window. */
export interface PostMessageTarget {
  postMessage(message: unknown, targetOrigin: string): void;
}

/** A received message event, narrowed to the fields the transport reads. */
export interface InboundMessageEvent {
  readonly origin: string;
  readonly data: unknown;
}

/** Unsubscribe handle for the inbound listener. */
export type Unsubscribe = () => void;

/** The injected environment (real browser window in production, fakes in tests). */
export interface GatewayEnvironment {
  /** Enumerate every discoverable frame to broadcast to (all ancestor levels), except self. */
  discoverableTargets(): readonly PostMessageTarget[];
  /** Subscribe to inbound messages; returns an unsubscribe handle. */
  addMessageListener(handler: (event: InboundMessageEvent) => void): Unsubscribe;
  /** Monotonic-ish timer for timeouts (injected for tests). */
  setTimer(callback: () => void, ms: number): number;
  clearTimer(handle: number): void;
  /** Correlation id generator (crypto.randomUUID in production). */
  newUUID(): string;
}

/** Transport-level options. */
export interface GatewayClientOptions {
  readonly origin: BridgeOriginConfig;
  readonly env: GatewayEnvironment;
  /** Per-request timeout in ms. Must be > 0. */
  readonly timeoutMs: number;
}

/**
 * Interpret a matched inbound reply against the target schema, normalizing the
 * current Gateway's legacy shape. A first-class boolean `ok` is authoritative;
 * `ok:false` must carry a structured BridgeError; when `ok` is absent (legacy
 * Gateway), a not-found result maps to unknown_function and any other result is
 * treated as a success `result`.
 */
export function interpretResponse(data: unknown): InterpretedResponse {
  if (typeof data !== "object" || data === null) {
    return { ok: false, error: makeBridgeError("malformed_message", "Reply is not an object.") };
  }
  const rec = data as Record<string, unknown>;
  const okField = rec["ok"];

  if (okField === true) {
    return { ok: true, result: rec["result"] };
  }
  if (okField === false) {
    const err = rec["error"];
    if (isBridgeError(err)) return { ok: false, error: err };
    return {
      ok: false,
      error: makeBridgeError(
        "system_error",
        "Gateway reported failure without a structured error.",
        typeof err === "string" ? err : undefined,
      ),
    };
  }

  return normalizeLegacyReply(rec);
}

/**
 * Normalize a legacy (pre-target) Gateway reply. A not-found result for an
 * unresolved module.function maps to unknown_function; an error-shaped nested
 * message maps to system_error; anything else is taken as a success result.
 */
function normalizeLegacyReply(rec: Record<string, unknown>): InterpretedResponse {
  const code = rec["code"];
  const status = rec["status"];
  const notFound =
    code === 404 ||
    status === 404 ||
    (typeof status === "string" && status.toLowerCase() === "not_found");
  if (notFound) {
    return {
      ok: false,
      error: makeBridgeError(
        "unknown_function",
        "The requested module.function did not resolve to a callable ZOHO.CREATOR function.",
      ),
    };
  }

  const nestedMessage = rec["message"];
  if (typeof nestedMessage === "object" && nestedMessage !== null) {
    const nm = nestedMessage as Record<string, unknown>;
    const nestedStatus = nm["status"];
    const nestedResponseText = nm["responseText"];
    const nestedStatusText = nm["statusText"];
    const nestedData = nm["data"];
    const nestedRecordStatus =
      typeof nestedData === "object" && nestedData !== null
        ? (nestedData as Record<string, unknown>)["status"]
        : undefined;
    const nestedFirstError =
      typeof nestedData === "object" && nestedData !== null
        ? (nestedData as Record<string, unknown>)["first_error"]
        : undefined;

    const hasErrorShape =
      typeof nestedStatus === "number" ||
      typeof nestedResponseText === "string" ||
      typeof nestedStatusText === "string" ||
      (nestedRecordStatus === "Error" && typeof nestedFirstError === "string" && nestedFirstError.length > 0);

    if (hasErrorShape) {
      const detail =
        typeof nestedResponseText === "string"
          ? nestedResponseText
          : typeof nestedFirstError === "string"
            ? nestedFirstError
            : typeof nestedStatusText === "string"
              ? nestedStatusText
              : undefined;
      return {
        ok: false,
        error: makeBridgeError(
          "system_error",
          "The Widget API Gateway reported an error response for the requested operation.",
          detail,
        ),
      };
    }
  }

  return { ok: true, result: rec };
}

/** A single in-flight request: its resolver and the timer that bounds it. */
interface Pending {
  resolve(value: InterpretedResponse): void;
  timer: number;
}

/**
 * The Gateway transport client. One instance owns one inbound listener and a map
 * of outstanding requests keyed by messageUUID. Call dispose() to tear down.
 */
export class GatewayClient {
  private readonly origin: BridgeOriginConfig;
  private readonly env: GatewayEnvironment;
  private readonly timeoutMs: number;
  private readonly pending = new Map<string, Pending>();
  private readonly unsubscribe: Unsubscribe;
  private disposed = false;

  constructor(options: GatewayClientOptions) {
    if (!(options.timeoutMs > 0)) {
      throw new Error("GatewayClient requires a positive timeoutMs.");
    }
    this.origin = options.origin;
    this.env = options.env;
    this.timeoutMs = options.timeoutMs;
    this.unsubscribe = this.env.addMessageListener((e) => this.onMessage(e));
  }

  /**
   * Invoke a Gateway-relayed ZOHO.CREATOR call and resolve with the interpreted
   * outcome. Never rejects: every terminal condition (success, structured
   * failure, timeout) resolves an InterpretedResponse so callers branch on an
   * explicit state (ADR-002 P5; ISO/IEC 5055 no-silent-fallback).
   */
  call(descriptor: CallDescriptor): Promise<InterpretedResponse> {
    if (this.disposed) {
      return Promise.resolve({
        ok: false,
        error: makeBridgeError("timeout", "Transport disposed before send."),
      });
    }
    const messageUUID = this.env.newUUID();
    const envelope: RequestEnvelope = makeRequestEnvelope(messageUUID, descriptor);

    return new Promise<InterpretedResponse>((resolve) => {
      const timer = this.env.setTimer(() => {
        if (this.pending.delete(messageUUID)) {
          resolve({
            ok: false,
            error: makeBridgeError(
              "timeout",
              "No correlated, origin-valid reply within the per-request timeout.",
            ),
          });
        }
      }, this.timeoutMs);

      this.pending.set(messageUUID, { resolve, timer });
      this.broadcast(envelope);
    });
  }

  /** Broadcast to every discoverable frame with the explicit target origin. */
  private broadcast(envelope: RequestEnvelope): void {
    const targets = this.env.discoverableTargets();
    for (const target of targets) {
      target.postMessage(envelope, this.origin.gatewayOrigin);
    }
  }

  /**
   * Inbound handler. A reply is actioned only when the origin matches the known
   * Gateway origin AND the messageUUID matches an outstanding request; otherwise
   * it is ignored without side effects (broadcast tolerance).
   */
  private onMessage(event: InboundMessageEvent): void {
    if (event.origin !== this.origin.gatewayOrigin) return;
    const uuid = readMessageUUID(event.data);
    if (uuid === null) return;
    const entry = this.pending.get(uuid);
    if (entry === undefined) return;

    this.pending.delete(uuid);
    this.env.clearTimer(entry.timer);
    entry.resolve(interpretResponse(event.data));
  }

  /** Tear down the listener and fail any still-outstanding requests. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.unsubscribe();
    for (const [uuid, entry] of this.pending) {
      this.env.clearTimer(entry.timer);
      entry.resolve({
        ok: false,
        error: makeBridgeError("timeout", "Transport disposed while awaiting reply."),
      });
      this.pending.delete(uuid);
    }
  }

  /** The protocol version this client sends. */
  get protocolVersion(): number {
    return PROTOCOL_VERSION;
  }
}

export type { BridgeError };
